// src/components/transport/ReglagesTransport.jsx
// ============================================================================
// Les réglages du bus : prix d'un seul sens, prix du ticket (lot 3a)
// ============================================================================
//
// Une section de la page « Paramétrage de la scolarité » (ce que l'école
// facture aux familles), selon PARAMETRAGE-MODULES.md : en-tête de section à
// pastille qui porte les boutons, puis la carte des réglages. Régime B
// (brouillon puis « Enregistrer »). Champs texte, jamais numériques (la
// molette retire 1). Absente tant que le registre du transport n'est pas
// installé.
// ============================================================================

import React, { useCallback, useEffect, useState } from 'react';
import { AlertCircle, Bus, RotateCcw, Save } from 'lucide-react';
import PorteurNotification from '../ui/PorteurNotification';
import EnTeteSection from '../parametrage/EnTeteSection';
import CarteReglages from '../parametrage/CarteReglages';
import { fcfa, montantMensuel, prixTicket, transport } from './apiTransport';

const ACCENT = '#319190';
const CHAMP = 'h-9 w-full rounded-lg border border-gray-200 bg-white px-3 pr-14 text-right text-sm text-gray-800 outline-none transition focus:border-[#319190] focus:ring-2 focus:ring-[#319190]/15';
const EXEMPLE = 10000;

const LIGNES = [
  {
    cle: 'pourcentage_un_sens', libelle: "Prix d'un seul sens", unite: '%',
    description: "Ce que paie un élève qui ne prend le bus qu'à l'aller ou qu'au retour, en pourcentage du tarif de sa zone (ou de sa classe). Vaut aussi pour la demi-journée au ticket."
  },
  {
    cle: 'ticket_diviseur', libelle: 'Jours de bus dans un mois', unite: 'jours',
    description: "Le ticket d'une journée coûte le tarif mensuel divisé par ce nombre (20 : un vingtième du mois). Le guichet ne tape plus le prix du ticket."
  }
];

const enTexte = (r) => ({ pourcentage_un_sens: String(r.pourcentage_un_sens), ticket_diviseur: String(r.ticket_diviseur) });

const ReglagesTransport = () => {
  const [lus, setLus] = useState(null);
  const [brouillon, setBrouillon] = useState(null);
  const [enregistrement, setEnregistrement] = useState(false);
  const [toast, setToast] = useState(null);

  const charger = useCallback(async () => {
    try {
      const r = await transport.reglages();
      setLus(r);
      setBrouillon(enTexte(r));
    } catch (e) {
      setToast({ message: e.message, type: 'error' });
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  if (!lus || !lus.installe || !brouillon) return <PorteurNotification notification={toast} onFermer={() => setToast(null)} />;

  const modifie = LIGNES.some((l) => String(lus[l.cle]) !== String(brouillon[l.cle]));
  const apercu = {
    pourcentage_un_sens: Number(brouillon.pourcentage_un_sens) || lus.pourcentage_un_sens,
    ticket_diviseur: Number(brouillon.ticket_diviseur) || lus.ticket_diviseur
  };

  const enregistrer = async () => {
    setEnregistrement(true);
    try {
      const r = await transport.enregistrerReglages({
        pourcentage_un_sens: Number(brouillon.pourcentage_un_sens),
        ticket_diviseur: Number(brouillon.ticket_diviseur)
      });
      setLus({ ...lus, ...r, installe: true });
      setBrouillon(enTexte(r));
      setToast({ message: 'Réglages du bus enregistrés', type: 'success' });
    } catch (e) {
      setToast({ message: e.message, type: 'error' });
    } finally {
      setEnregistrement(false);
    }
  };

  return (
    <div className="space-y-5 border-t border-gray-100 pt-6">
      <PorteurNotification notification={toast} onFermer={() => setToast(null)} />
      <EnTeteSection
        icone={Bus}
        titre="Le bus scolaire"
        sousTitre="Le prix d'un seul sens et celui du ticket, pour tous les élèves"
        accent={ACCENT}
        accentSombre="#2a7a79"
        actions={(
          <>
            <button
              type="button"
              onClick={() => setBrouillon(enTexte(lus))}
              disabled={!modifie || enregistrement}
              className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3.5 py-2 text-xs font-semibold text-gray-500 transition-all hover:border-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <RotateCcw size={13} /> Réinitialiser
            </button>
            <button
              type="button"
              onClick={enregistrer}
              disabled={!modifie || enregistrement}
              className={`flex items-center gap-1.5 rounded-lg border border-transparent px-4 py-2 text-xs font-semibold text-white shadow-sm transition-all ${modifie ? 'bg-[#319190] hover:shadow-md' : 'cursor-not-allowed bg-gray-300 shadow-none'}`}
            >
              {enregistrement ? <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white" /> : <Save size={13} />}
              Enregistrer
            </button>
          </>
        )}
      />
      <CarteReglages titre="Prix du bus" accent={ACCENT} compteur="2 réglages" dense>
        {LIGNES.map((l) => (
          <div key={l.cle} className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 hover:bg-gray-50/70">
            <div className="flex min-w-[220px] flex-1 items-center gap-3">
              <div className="h-1.5 w-1.5 flex-shrink-0 rounded-full" style={{ backgroundColor: ACCENT }} />
              <div>
                <label htmlFor={`transport-${l.cle}`} className="text-sm font-semibold text-gray-800">{l.libelle}</label>
                <p className="mt-0.5 text-[11px] leading-relaxed text-gray-400">{l.description}</p>
              </div>
            </div>
            <div className="relative w-36">
              <input
                id={`transport-${l.cle}`}
                type="text"
                inputMode="numeric"
                autoComplete="off"
                className={CHAMP}
                value={brouillon[l.cle]}
                onChange={(e) => setBrouillon((b) => ({ ...b, [l.cle]: e.target.value.replace(/[^\d]/g, '') }))}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">{l.unite}</span>
            </div>
          </div>
        ))}
        <p className="px-5 py-3 text-[11px] text-gray-500">
          Pour un tarif de {fcfa(EXEMPLE)} par mois : un seul sens {fcfa(montantMensuel(EXEMPLE, 'aller', apercu.pourcentage_un_sens))},
          ticket journée {fcfa(prixTicket(EXEMPLE, 'journee', apercu))}, demi-journée {fcfa(prixTicket(EXEMPLE, 'demi_journee', apercu))}.
        </p>
      </CarteReglages>
      {modifie && (
        <div className="flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
          <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-amber-100"><AlertCircle size={15} className="text-amber-600" /></div>
          <p className="text-xs font-semibold text-amber-700">Vous avez des modifications non enregistrées.</p>
          <button type="button" onClick={enregistrer} disabled={enregistrement} className="ml-auto text-xs font-semibold text-amber-800 underline">
            {enregistrement ? 'Enregistrement…' : 'Enregistrer maintenant'}
          </button>
        </div>
      )}
    </div>
  );
};

export default ReglagesTransport;
